import React, { useRef, useEffect } from "react";
import { ChevronDown, Check } from "lucide-react";

export interface CustomSelectOption {
  value: string;
  label: string;
}

interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: CustomSelectOption[];
  placeholder?: string;
  className?: string;
  error?: boolean;
  disabled?: boolean;
  name?: string;
  id?: string;
  variant?: "default" | "filter";
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = "Select...",
  className = "",
  error = false,
  disabled = false,
  name,
  id,
  variant = "default",
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((option) => option.value === value);

  const displayText = selectedOption
    ? selectedOption.label
    : placeholder;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Keyboard interaction
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    if (e.key === "Escape") {
      setIsOpen(false);
    }

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setIsOpen((prev) => !prev);
    }
  };

  return (
    <div
      ref={containerRef}
      className={`relative ${className}`}
      id={id}
    >
      <button
        type="button"
        name={name}
        disabled={disabled}
        onClick={() => {
          if (!disabled) {
            setIsOpen((prev) => !prev);
          }
        }}
        onKeyDown={handleKeyDown}
        className={`text-left flex items-center justify-between rounded-lg transition-colors w-full h-full ${
          variant === "filter"
            ? "bg-primary-600 hover:bg-primary-700 text-white px-3 shadow-sm"
            : "input-field"
        } ${
          error ? "border-red-500" : ""
        } ${
          disabled
            ? "opacity-50 cursor-not-allowed"
            : "cursor-pointer"
        }`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span
          className={
            !selectedOption
              ? "text-neutral-400"
              : variant === "filter"
              ? "text-white"
              : "text-neutral-800"
          }
        >
          {displayText}
        </span>

        <ChevronDown
          aria-hidden="true"
          className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${
            variant === "filter"
              ? "text-white/80"
              : "text-neutral-400"
          } ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div
          className="absolute z-50 mt-1 w-full bg-white border border-neutral-200 rounded-lg shadow-lg overflow-hidden max-h-60 overflow-y-auto"
          role="listbox"
        >
          {options.map((option) => {
            const isSelected = value === option.value;

            return (
              <div
                key={option.value}
                className={`px-3 py-2.5 cursor-pointer text-sm transition-colors flex items-center justify-between ${
                  isSelected
                    ? "bg-primary-50 text-neutral-800 font-medium"
                    : "bg-white text-neutral-800 hover:bg-primary-50"
                }`}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                role="option"
                aria-selected={isSelected}
              >
                <span>{option.label}</span>

                {isSelected && (
                  <Check
                    aria-hidden="true"
                    className="h-4 w-4 text-primary-600 flex-shrink-0"
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CustomSelect;